import { Button, Flex, Form, Input, Spin, Text, Title } from '@bb/elements-web';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { uploadFiles } from 'api/AssetController';
import { getMetaData, type VideoMetaData } from 'api/MetaDataController';
import { message, Modal } from 'components';
import useBreakpoint from 'hooks/useBreakpoint';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ASSET_MODAL_LG_WIDTH } from '../constants';

export interface AddVideoModalProps {
  visible: boolean;
  parent: string | undefined;
  onClose: () => void;
}

const AddVideoModal: React.FC<AddVideoModalProps> = ({
  visible,
  parent,
  onClose,
}) => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const screen = useBreakpoint();
  const isLessThanLarge = !screen.lg;
  const [videoLink, setVideoLink] = useState('');
  const [validationResult, setValidationResult] = useState<
    VideoMetaData & { error?: string }
  >();

  useEffect(() => {
    if (visible) {
      form.resetFields();
      setVideoLink('');
      setValidationResult(undefined);
    }
  }, [visible, form]);

  // Validate video link mutation
  const validateMutation = useMutation({
    mutationFn: (link: string) => getMetaData(link),
    onSuccess: data => {
      if (data.title) {
        setValidationResult(data);
      } else {
        setValidationResult({
          error: 'Please provide a valid non-private link.',
          title: null,
          embedUrl: '',
          thumbnail: null,
        });
      }
    },
    onError: (error: any) => {
      setValidationResult({
        error:
          error?.response?.data?.message || 'Failed to validate video link',
        title: null,
        embedUrl: '',
        thumbnail: null,
      });
    },
  });

  // Save video mutation
  const saveVideoMutation = useMutation({
    mutationFn: async () => {
      if (!validationResult || validationResult.error) {
        throw new Error('Invalid video');
      }

      const videoLinkFile = [
        {
          filename: validationResult.title,
          mimetype: 'application/x-shockwave-flash',
          url: videoLink,
          thumbnail: validationResult.thumbnail,
          embedUrl: validationResult.embedUrl,
        },
      ];

      return uploadFiles(videoLinkFile, parent);
    },
    onSuccess: () => {
      message.success('Video added successfully');
      queryClient.invalidateQueries({ queryKey: ['folders'] });
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      setVideoLink('');
      setValidationResult(undefined);
      form.resetFields();
      onClose();
    },
    onError: () => {
      message.error('Failed to save video');
    },
  });

  const handleValidate = () => {
    if (!videoLink) {
      setValidationResult({
        error: 'Please provide a link.',
        title: null,
        embedUrl: '',
        thumbnail: null,
      });
      return;
    }
    validateMutation.mutate(videoLink);
  };

  const handleVideoLinkChange = (value: string) => {
    setVideoLink(value);
    setValidationResult(undefined);
  };

  const handleSave = () => {
    saveVideoMutation.mutate();
  };

  const handleSubmit = () => {
    if (isValid) {
      handleSave();
    } else {
      handleValidate();
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const isValid = validationResult && !validationResult.error;
  const loading = validateMutation.isPending || saveVideoMutation.isPending;

  const loadingMessage = useMemo(() => {
    if (!validateMutation.isPending && !saveVideoMutation.isPending)
      return null;

    const loadingText = validateMutation.isPending
      ? 'Validating Video Link...'
      : 'Saving Video...';

    return (
      <Flex vertical justify="center" align="center" gap={16}>
        <Text size="large">{loadingText}</Text>
        <Spin dataCy="add-video-modal-loading-spinner" size="large" />
      </Flex>
    );
  }, [validateMutation.isPending, saveVideoMutation.isPending]);

  return (
    <Modal
      centered
      width={isLessThanLarge ? '85%' : ASSET_MODAL_LG_WIDTH}
      destroyOnClose
      title={
        <StyledModalTitle
          dataCy="add-video-modal-title"
          level={3}
          thickness="normal"
        >
          Add Video
        </StyledModalTitle>
      }
      open={visible}
      onCancel={handleCancel}
      confirmLoading={loading}
      footer={[
        <Flex
          key="1-add-video-modal-footer-container"
          align="center"
          justify="flex-start"
        >
          <Button
            key="1-validate-button"
            minWidth={65}
            size="small"
            type="primary"
            onClick={handleValidate}
            loading={validateMutation.isPending}
            dataCy="add-video-modal-validate-button"
          >
            {validateMutation.isPending ? 'Validating...' : 'Validate'}
          </Button>
          <StyledFooterButtonContainer>
            <Button
              key="2-cancel-button"
              minWidth={65}
              size="small"
              type="secondary"
              color="default"
              onClick={handleCancel}
              dataCy="add-video-modal-cancel-button"
            >
              Close
            </Button>
            <Button
              key="3-save-button"
              minWidth={65}
              size="small"
              type="primary"
              onClick={handleSave}
              disabled={!isValid}
              loading={saveVideoMutation.isPending}
              dataCy="add-video-modal-save-button"
            >
              Save
            </Button>
          </StyledFooterButtonContainer>
        </Flex>,
      ]}
    >
      {loadingMessage}
      {!loading && (
        <Form
          dataCy="add-video-form"
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            label="Paste a YouTube or Vimeo link below:"
            name="videoLink"
            validateStatus={validationResult?.error ? 'error' : undefined}
            help={validationResult?.error}
          >
            <Input
              dataCy="add-video-modal-url-input"
              placeholder="Video URL"
              value={videoLink}
              onChange={handleVideoLinkChange}
            />
          </Form.Item>

          {isValid && validationResult && (
            <StyledSuccessContainer>
              <Flex vertical align="center" gap={16}>
                {validationResult.thumbnail && (
                  <StyledThumbnail
                    src={validationResult.thumbnail}
                    alt={validationResult.title}
                  />
                )}
                <Text dataCy="add-video-modal-title-text">
                  {validationResult.title}
                </Text>
              </Flex>
            </StyledSuccessContainer>
          )}
        </Form>
      )}
    </Modal>
  );
};

export default AddVideoModal;

const StyledModalTitle = styled(Title)`
  && {
    margin: 0;
  }
`;

const StyledFooterButtonContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-end;
  gap: 6px;
`;

const StyledSuccessContainer = styled.div`
  background-color: #dff0d8;
  border: 1px solid #d6e9c6;
  border-radius: 4px;
  padding: 15px;
  margin-top: 15px;
`;

const StyledThumbnail = styled.img`
  max-width: 50%;
  margin: auto;
  display: block;
`;
